import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import type { RootState } from "@/app/store/store";
import { ROLE_SCOPE, STATUS, type RoleScope } from "@/constants";
import { apiFetch } from "@/shared/api/apiFetch";
import { readApiErrorMessage } from "@/shared/api/parseApiErrorMessage";

export type CreateRolePayload = {
  name: string;
  scope: RoleScope;
  permission_codes: string[];
};

/** Scope is immutable after creation, so an update never carries it. */
export type UpdateRolePayload = {
  id: number;
  name: string;
  permission_codes: string[];
};

/**
 * One tickable row on the Roles page. The picker grants groups, not individual codes: ticking a
 * group writes every code in `codes`. Labels are ours (see `locales/en/roles.ts`), keyed by `key`
 * and `area` -- the backend owns the structure, the frontend owns the words.
 */
export type PermissionGroup = {
  key: string;
  area: string;
  codes: string[];
  /** Keys of groups this one is useless without; ticking this ticks those too. */
  requires: string[];
};

export type Role = {
  id: number;
  name: string;
  scope: RoleScope;
  permissions: string[];
  created_at: string;
};

export type RolesState = {
  status: "idle" | "loading" | "succeeded" | "failed";
  error: string | null;
  roles: Role[];
  permissionGroupsStatus: "idle" | "loading" | "succeeded" | "failed";
  permissionGroups: PermissionGroup[];
  /** Which scope `permissionGroups` was last loaded for, so the modal refetches on a change. */
  permissionGroupsScope: RoleScope | null;
};

export const rolesInitialState: RolesState = {
  status: STATUS.IDLE,
  error: null,
  roles: [],
  permissionGroupsStatus: STATUS.IDLE,
  permissionGroups: [],
  permissionGroupsScope: null,
};

function isRole(value: unknown): value is Role {
  if (typeof value !== "object" || value === null) {
    return false;
  }
  const row = value as Record<string, unknown>;
  return (
    typeof row.id === "number" &&
    typeof row.name === "string" &&
    (row.scope === ROLE_SCOPE.TENANT || row.scope === ROLE_SCOPE.DOMAIN) &&
    Array.isArray(row.permissions) &&
    row.permissions.every((permission) => typeof permission === "string") &&
    typeof row.created_at === "string"
  );
}

function parseRolesResponse(data: unknown): Role[] | null {
  if (!Array.isArray(data)) {
    return null;
  }
  if (!data.every(isRole)) {
    return null;
  }
  return data;
}

function isStringArray(value: unknown): value is string[] {
  return Array.isArray(value) && value.every((entry) => typeof entry === "string");
}

function isPermissionGroup(value: unknown): value is PermissionGroup {
  if (typeof value !== "object" || value === null) {
    return false;
  }
  const row = value as Record<string, unknown>;
  return (
    typeof row.key === "string" &&
    typeof row.area === "string" &&
    isStringArray(row.codes) &&
    isStringArray(row.requires)
  );
}

function parsePermissionGroupsResponse(data: unknown): PermissionGroup[] | null {
  if (!Array.isArray(data)) {
    return null;
  }
  if (!data.every(isPermissionGroup)) {
    return null;
  }
  return data;
}

export const fetchRoles = createAsyncThunk<
  Role[],
  void,
  { rejectValue: string }
>("roles/fetch", async (_, { rejectWithValue }) => {
  try {
    const res = await apiFetch("/v1/roles", { method: "GET" });
    if (!res.ok) {
      return rejectWithValue(`Request failed (${res.status})`);
    }
    const data: unknown = await res.json();
    const roles = parseRolesResponse(data);
    if (!roles) {
      return rejectWithValue("Unexpected response shape");
    }
    return roles;
  } catch (error) {
    const message = error instanceof Error ? error.message : "Request failed";
    return rejectWithValue(message);
  }
});

export const createRole = createAsyncThunk<
  Role,
  CreateRolePayload,
  { rejectValue: string }
>("roles/create", async (payload, { rejectWithValue }) => {
  try {
    const res = await apiFetch("/v1/roles", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(payload),
    });
    if (!res.ok) {
      return rejectWithValue(await readApiErrorMessage(res));
    }
    const data: unknown = await res.json();
    if (!isRole(data)) {
      return rejectWithValue("Unexpected response shape");
    }
    return data;
  } catch (error) {
    const message = error instanceof Error ? error.message : "Request failed";
    return rejectWithValue(message);
  }
});

export const updateRole = createAsyncThunk<
  Role,
  UpdateRolePayload,
  { rejectValue: string }
>("roles/update", async ({ id, ...payload }, { rejectWithValue }) => {
  try {
    const res = await apiFetch(`/v1/roles/${id}`, {
      method: "PATCH",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(payload),
    });
    if (!res.ok) {
      return rejectWithValue(await readApiErrorMessage(res));
    }
    const data: unknown = await res.json();
    if (!isRole(data)) {
      return rejectWithValue("Unexpected response shape");
    }
    return data;
  } catch (error) {
    const message = error instanceof Error ? error.message : "Request failed";
    return rejectWithValue(message);
  }
});

/**
 * The permission groups offered for a role of `scope`. Refetched on a scope change: a domain
 * role is offered a subset of the workspace groups, so the picker is rebuilt, not filtered.
 */
export const fetchPermissionGroups = createAsyncThunk<
  PermissionGroup[],
  RoleScope,
  { rejectValue: string }
>("roles/fetchPermissionGroups", async (scope, { rejectWithValue }) => {
  try {
    const res = await apiFetch(`/v1/roles/permission-groups?scope=${scope}`, { method: "GET" });
    if (!res.ok) {
      return rejectWithValue(`Request failed (${res.status})`);
    }
    const data: unknown = await res.json();
    const groups = parsePermissionGroupsResponse(data);
    if (!groups) {
      return rejectWithValue("Unexpected response shape");
    }
    return groups;
  } catch (error) {
    const message = error instanceof Error ? error.message : "Request failed";
    return rejectWithValue(message);
  }
});

const rolesSlice = createSlice({
  name: "roles",
  initialState: rolesInitialState,
  reducers: {},
  extraReducers(builder) {
    builder
      .addCase(fetchRoles.pending, (state) => {
        state.status = STATUS.LOADING;
        state.error = null;
      })
      .addCase(fetchRoles.fulfilled, (state, action) => {
        state.status = STATUS.SUCCEEDED;
        state.roles = action.payload;
      })
      .addCase(fetchRoles.rejected, (state) => {
        state.status = STATUS.SUCCEEDED;
        state.roles = [];
        state.error = null;
      })
      .addCase(updateRole.fulfilled, (state, action) => {
        const index = state.roles.findIndex((role) => role.id === action.payload.id);
        if (index !== -1) {
          state.roles[index] = action.payload;
        }
      })
      .addCase(fetchPermissionGroups.pending, (state, action) => {
        state.permissionGroupsStatus = STATUS.LOADING;
        state.permissionGroupsScope = action.meta.arg;
      })
      .addCase(fetchPermissionGroups.fulfilled, (state, action) => {
        state.permissionGroupsStatus = STATUS.SUCCEEDED;
        state.permissionGroups = action.payload;
      })
      .addCase(fetchPermissionGroups.rejected, (state) => {
        state.permissionGroupsStatus = STATUS.FAILED;
        state.permissionGroups = [];
      });
  },
});

export const rolesReducer = rolesSlice.reducer;

export const selectRoles = (state: RootState) => state.roles.roles;
export const selectRolesStatus = (state: RootState) => state.roles.status;
export const selectPermissionGroups = (state: RootState) => state.roles.permissionGroups;
export const selectPermissionGroupsStatus = (state: RootState) =>
  state.roles.permissionGroupsStatus;
export const selectPermissionGroupsScope = (state: RootState) => state.roles.permissionGroupsScope;
